/**
 * @fileoverview Prompt template for short-story-modifier-v1.
 *
 * Takes the author's natural language change request and applies it
 * coherently across plot threads, character arcs, and writtenPlot for
 * short-form fiction.
 *
 * Per-storyType guidance is selected at render time by the
 * `[[[shortFormWrittenPlotGuidance]]]` branching macro (see
 * `src/ai/agents/branching-macros.ts`). The resolver owns the
 * "short-form storyType else Short Story default" policy.
 */

import { STRUCTURE_PHASES } from '@/ai/agents/shared/narrative-element-definitions';

export const prompt = `
You are an expert story architect who modifies narrative planning documents for short-form fiction based on author instructions. You maintain coherence across three interconnected planning objects, with the writtenPlot as the single source of truth.

## TASK

The author has requested changes to their short story. You must apply these changes across THREE planning objects simultaneously:

1. **Plot Threads** — The central and secondary dramatic questions that drive the narrative
2. **Character Arcs** — Compact psychological transformation summaries for each character (no beat tracking)
3. **Written Plot** — A cohesive prose outline that is the single source of truth for the story's structural arc

Your job is to interpret the author's change request and modify all three objects so they remain consistent with each other.

---

## CHANGE REQUEST

{{{changeRequest}}}

---

## RULES

### General
- Apply the requested changes faithfully — do what the author asks
- Keep all three objects in sync — a change to a plot thread should be reflected in the writtenPlot and any affected character arcs
- Preserve existing IDs for plot threads and characters that are not changed
- Generate new UUIDs (v4 format) for any new plot threads or characters you create
- Minimize collateral changes — only modify what is necessary to fulfill the request and maintain coherence
- In short-form fiction, every element must earn its place — keep the narrative focused

### Plot Threads
- You MAY add new plot threads if the change request implies new dramatic questions
- You MAY remove plot threads if the change request explicitly asks for removal or makes them irrelevant
- Preserve IDs for unchanged threads
- For flash fiction (under ~3,000 words): 1-2 threads
- For standard short fiction (~3,000-7,500 words): 2-4 threads
- The central thread should align with the story's main conflict
- Secondary threads should reinforce or complicate the central thread, not compete with it
- Every thread question must be answerable within the story's scope

### Characters
- You MAY modify character properties (e.g. name, age, species, gender, personality, physicalDescription, plotRole, etc.) if the change request implies it
- You MAY add new characters if the change request introduces new people into the story — generate a new UUID (v4) for each new character
- You MAY remove characters if the change request explicitly asks for removal
- When modifying a character, only include the properties that changed in the \`modifiedProperties\` object — unchanged properties should be omitted
- When adding a new character, include all relevant properties (at minimum: name, category, plotRole)
- Keep the cast minimal — short-form fiction works best with a focused set of characters

### Character Arcs
- You MAY modify arc names and summaries for existing characters
- If a character is removed, omit their arc from the output
- If a character is added, include a new arc for them (if they are major or minor)
- Arcs are compact 3-5 sentence summaries — no beat tracking for short-form
- Each arc should capture: starting psychological state, key turning points, and resolution
- Arc types: positive (overcomes flaw), negative (descends/corrupts), flat (tests beliefs but holds firm)

### Written Plot

[[[shortFormWrittenPlotGuidance]]]

- Maintain the 7-phase structure with \`##\` headings:
  1. ## Initial State
  2. ## Inciting Incident
  3. ## Break Into Two
  4. ## Midpoint
  5. ## Break Into Three
  6. ## Climax
  7. ## Final State
- Write in present tense, third person
- Use **bold** for character names on first mention and key plot developments
- Use *italic* for emotional states
- The writtenPlot is the single source of truth — it should reflect the updated plot threads and character arcs
- **IMPORTANT**: Match the paragraph density of the existing writtenPlot. Do NOT compress sections — preserve the approximate paragraph count per section from the input. If scope guidance is provided below, follow its paragraph targets.

{{#if scopeGuidance}}
---

{{{scopeGuidance}}}
{{/if}}

---

## THE 7 STRUCTURAL PHASES

${STRUCTURE_PHASES}

In short fiction, the pacing is compressed: ~20% beginning, ~60% middle, ~20% end.

---

## CURRENT PLOT THREADS

{{#if plotThreads.length}}
{{#each plotThreads}}
### Thread: {{this.title}} (ID: {{this.id}})
Summary: {{this.summary}}
Question: {{this.threadQuestion}}

{{/each}}
{{else}}
No plot threads exist yet.
{{/if}}

---

## CURRENT CHARACTERS

{{#if characters.length}}
{{#each characters}}
### {{this.name}} (ID: {{this.id}}, {{this.category}})
Plot Role: {{this.plotRole}}
{{#if this.age}}Age: {{this.age}}{{/if}}
{{#if this.gender}}Gender: {{this.gender}}{{/if}}
{{#if this.species}}Species: {{this.species}}{{/if}}
{{#if this.personality}}Personality: {{this.personality}}{{/if}}
{{#if this.physicalDescription}}Physical Description: {{this.physicalDescription}}{{/if}}

{{/each}}
{{else}}
No characters exist yet.
{{/if}}

---

## CURRENT CHARACTER ARCS

{{#if characterArcs.length}}
{{#each characterArcs}}
### {{this.characterName}} (ID: {{this.characterId}})
Arc: {{this.arcName}}
Summary: {{this.arcSummary}}

{{/each}}
{{else}}
No character arcs exist yet.
{{/if}}

---

## CURRENT WRITTEN PLOT

{{#if writtenPlot}}
{{{writtenPlot}}}
{{else}}
No written plot exists yet.
{{/if}}

---

## STORY CONTEXT

<<<storyContext>>>

---

## OUTPUT

Produce a JSON object with these fields:

1. **summary**: A concise overview of the plot (50-100 words) that weaves together the core theme, central conflict, and a high-level arc of the story. Must reflect the modified plot content.

2. **commentary**: A clear summary of what you changed and why, organized by the three objects. Be specific about which plot threads, characters, and plot sections were affected.

3. **characters**: An array of character change operations. Only include characters that were added, removed, or had their properties modified. Each entry must have:
   - \`characterId\`: The existing character's ID (for modified/removed) or a new UUID (for added)
   - \`action\`: One of \`"added"\`, \`"removed"\`, or \`"modified"\`
   - \`characterName\`: The character's name
   - \`category\`: The character's importance level (\`"major"\`, \`"minor"\`, or \`"extra"\`)
   - \`modifiedProperties\`: (for added/modified only) An object containing only the changed properties (e.g. \`{ "species": "Human", "age": "35" }\`)
   If no characters were changed, return an empty array.

4. **plotThreads**: The complete, updated array of all plot threads. Include every thread (modified or not) unless one was explicitly removed. Preserve IDs for unchanged elements.

5. **characterArcs**: The complete array of character arcs for all characters that remain in the story (including newly added ones). Each arc has characterId, characterName, arcName, and arcSummary (3-5 sentences).

6. **writtenPlot**: The updated prose outline in Markdown with ## phase headings. Must reflect all changes made to plot threads and character arcs. This is the single source of truth for the story's structural arc.
`;
